'use client';

import { useState } from 'react';

interface StudentLoanState {
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
}

const INITIAL_STATE: StudentLoanState = {
  loanAmount: 50000,
  interestRate: 4.5,
  loanTerm: 10,
};

const PRESETS: { [key: string]: StudentLoanState } = {
  conservative: {
    loanAmount: 30000,
    interestRate: 3.5,
    loanTerm: 10,
  },
  moderate: {
    loanAmount: 50000,
    interestRate: 4.5,
    loanTerm: 10,
  },
  aggressive: {
    loanAmount: 80000,
    interestRate: 6.0,
    loanTerm: 15,
  },
};

export default function CalculatorStudentLoan() {
  const [inputs, setInputs] = useState<StudentLoanState>(INITIAL_STATE);

  const calculate = () => {
    const monthlyRate = inputs.interestRate / 100 / 12;
    const months = inputs.loanTerm * 12;
    const monthlyPayment =
      inputs.loanAmount *
      (monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);
    const totalPaid = monthlyPayment * months;
    const totalInterest = totalPaid - inputs.loanAmount;

    return {
      monthlyPayment,
      totalPaid,
      totalInterest,
    };
  };

  const results = calculate();

  const handleReset = () => {
    setInputs(INITIAL_STATE);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    setInputs(PRESETS[preset]);
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-2">
        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200 sticky top-24">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-2 h-8 bg-gradient-to-b from-orange-500 to-amber-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-slate-900">Loan Information</h2>
          </div>

          {/* Preset and Reset Buttons */}
          <div className="mb-6 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => handlePreset('conservative')}
                className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
              >
                Conservative
              </button>
              <button
                onClick={() => handlePreset('moderate')}
                className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
              >
                Moderate
              </button>
              <button
                onClick={() => handlePreset('aggressive')}
                className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
              >
                Aggressive
              </button>
            </div>
            <button
              onClick={handleReset}
              className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
            >
              Reset to Default
            </button>
          </div>

          <div className="space-y-6">
            <InputField
              label="Total Loan Amount"
              value={inputs.loanAmount}
              onChange={(v) => setInputs({ ...inputs, loanAmount: v })}
              min={1000}
              prefix="$"
            />
            <InputField
              label="Interest Rate"
              value={inputs.interestRate}
              onChange={(v) => setInputs({ ...inputs, interestRate: v })}
              min={0}
              max={15}
              step={0.1}
              unit="%"
            />
            <InputField
              label="Repayment Term"
              value={inputs.loanTerm}
              onChange={(v) => setInputs({ ...inputs, loanTerm: v })}
              min={1}
              max={30}
              unit="years"
            />
          </div>
        </div>
      </div>

      <div className="lg:col-span-3 space-y-6">
        <div className="bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-600 rounded-3xl p-10 shadow-modern-xl text-white">
          <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">
            Monthly Payment
          </div>
          <div className="text-6xl font-bold mb-2">
            ${results.monthlyPayment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-lg opacity-90">
            For {inputs.loanTerm * 12} months ({inputs.loanTerm} years)
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <ResultCard
            label="Total Interest"
            value={`$${results.totalInterest.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Cost of education financing"
            gradient="from-red-500 to-orange-600"
          />
          <ResultCard
            label="Total Repayment"
            value={`$${results.totalPaid.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            description="Principal + interest"
            gradient="from-blue-500 to-indigo-600"
          />
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200">
          <h3 className="text-xl font-bold text-slate-900 mb-6">Repayment Summary</h3>
          <div className="space-y-4">
            <InsightRow label="Loan amount" value={`$${inputs.loanAmount.toLocaleString('en-US')}`} />
            <InsightRow label="Interest rate" value={`${inputs.interestRate}%`} />
            <InsightRow label="Repayment term" value={`${inputs.loanTerm} years`} />
            <InsightRow
              label="Interest as % of loan"
              value={`${((results.totalInterest / inputs.loanAmount) * 100).toFixed(1)}%`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  unit?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className={`w-full ${prefix ? 'pl-8' : 'pl-4'} ${unit ? 'pr-16' : 'pr-4'} py-3.5 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20 outline-none transition-all font-semibold text-slate-900 text-lg`}
        />
        {unit && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultCard({
  label,
  value,
  description,
  gradient,
}: {
  label: string;
  value: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 shadow-modern-lg text-white`}>
      <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">{label}</div>
      <div className="text-4xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-90">{description}</div>
    </div>
  );
}

function InsightRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-3 border-b border-slate-100 last:border-0">
      <span className="text-slate-600 font-medium">{label}</span>
      <span className="text-slate-900 font-bold text-lg">{value}</span>
    </div>
  );
}